/**
 * Auth Context
 * Global authentication state management
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Check if user has tokens stored
      const token = authService.isAuthenticated();
      const refreshToken = localStorage.getItem('refreshToken');

      if (token || refreshToken) {
        // Get user from localStorage first (for instant UI)
        const cachedUser = authService.getUser();
        if (cachedUser) {
          setUser(cachedUser);
        }

        // Then fetch fresh user data from server
        try {
          const freshUserData = await authService.getCurrentUser();
          setUser(freshUserData);
        } catch (err) {
          console.error('Failed to fetch fresh user data:', err);

          // If we have cached user but fetch fails, keep the cached user
          // The API service will handle token refresh automatically
          // Only logout if explicitly unauthorized
          if (err.message.includes('Unauthorized') || err.message.includes('Invalid token')) {
            authService.logout();
            setUser(null);
          } else if (!cachedUser) {
            // No cached user and fetch failed
            authService.logout();
            setUser(null);
          }
        }
      } else {
        // No tokens - user is not logged in, just set user to null
        // Don't call logout() as there's nothing to log out from
        setUser(null);
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setError(err.message);
      authService.logout();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      const data = await authService.login(email, password);
      setUser(data.user);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await authService.signup(userData);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    updateUser,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
